import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { Player, PlayerWithLeagueContext, RatingHistory } from '../../generated-api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';

// Helper function to detect touch devices
const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin
);

interface PlayerRatingsChartProps {
  players: (Player | PlayerWithLeagueContext)[];
  playerHistories: Record<string, RatingHistory>;
  title?: string;
  emptyMessage?: string;
  className?: string;
  showConservativeToggle?: boolean;
}

const PlayerRatingsChart: React.FC<PlayerRatingsChartProps> = ({
  players,
  playerHistories,
  title = "Rating History - All Players",
  emptyMessage = "No rating history available yet.",
  className = "",
  showConservativeToggle = true,
}) => {
  const [activeTab, setActiveTab] = useState<'ratings' | 'conservative'>('ratings');
  const [isZoomed, setIsZoomed] = useState(false);
  const chartRef = useRef<ChartJS<'line', (number | null)[], unknown>>(null);

  const generateChartData = useCallback(() => {
    const colors = [
      'rgb(59, 130, 246)',
      'rgb(16, 185, 129)', 
      'rgb(245, 101, 101)',
      'rgb(139, 92, 246)',
      'rgb(249, 115, 22)',
      'rgb(236, 72, 153)',
      'rgb(14, 165, 233)',
      'rgb(34, 197, 94)',
      'rgb(168, 85, 247)',
      'rgb(251, 146, 60)'
    ];

    const playerData: Array<{ player: Player | PlayerWithLeagueContext; history: RatingHistory }> = [];
    
    players.forEach(player => {
      const history = playerHistories[player.id];
      if (history && history.ratingProgression) {
        playerData.push({ player, history });
      }
    });

    if (playerData.length === 0) return null;

    // Collect all unique play IDs across all players and sort by date
    const allPlays = new Map<string, string>(); // playId -> playDate
    playerData.forEach(({ history }) => {
      history.ratingProgression.forEach(entry => {
        allPlays.set(entry.playId, entry.playDate);
      });
    });
    
    // Sort plays chronologically by date, then by playId for consistency
    const sortedPlayIds = Array.from(allPlays.entries())
      .sort(([playIdA, dateA], [playIdB, dateB]) => {
        const dateComparison = dateA.localeCompare(dateB);
        return dateComparison !== 0 ? dateComparison : playIdA.localeCompare(playIdB);
      })
      .map(([playId]) => playId);

    // Create labels based on chronological play order
    const labels = ['Start', ...sortedPlayIds.map((_, index) => `Game ${index + 1}`)];

    // Generate datasets for each player
    const datasets = playerData.flatMap((data, index) => {
      const { player, history } = data;
      const color = colors[index % colors.length];
      
      // Create a map of playId to rating for this player
      const playerRatings = new Map<string, {rating: number, sigma: number}>();
      history.ratingProgression.forEach(entry => {
        playerRatings.set(entry.playId, {
          rating: Number(entry.ratingAfter.rating.toFixed(2)),
          sigma: Number(entry.ratingAfter.sigma.toFixed(2))
        });
      });
      
      // Create rating data points aligned by chronological play order
      const ratings: (number | null)[] = []; 
      const conservativeRatings: (number | null)[] = [];
      
      // Initial TrueSkill rating (before first game)
      ratings.push(25.0);
      conservativeRatings.push(25.0 - 3 * 8.33);
      
      // For each chronological play, check if this player participated
      sortedPlayIds.forEach((playId) => {
        if (playerRatings.has(playId)) {
          const { rating, sigma } = playerRatings.get(playId)!;
          // Player participated in this play, add their rating after this game
          ratings.push(rating);
          conservativeRatings.push(rating - 3 * sigma);
        } else {
          // Player didn't participate in this play, skip data point (null)
          ratings.push(null);
          conservativeRatings.push(null);
        }
      });
      
      // If player has never played, show only their initial rating (25.0)
      if (history.ratingProgression.length === 0) {
        const playerWithLeague = player as PlayerWithLeagueContext;
        const currentRating = (playerWithLeague.leagueRating) ? Number(playerWithLeague.leagueRating.rating.toFixed(2)) : 25.0;
        const currentSigma = (playerWithLeague.leagueRating) ? Number(playerWithLeague.leagueRating.sigma.toFixed(2)) : 8.33;
        ratings[0] = currentRating;
        conservativeRatings[0] = currentRating - 3 * currentSigma;
      }

      const ratingDataset: any = {
        label: player.name,
        data: ratings,
        borderColor: color,
        backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        spanGaps: true, // Connect points even when some are null
        isConservative: false,
      };

      const conservativeRatingDataset: any = {
        label: player.name,
        data: conservativeRatings,
        borderColor: color,
        backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        spanGaps: true, // Connect points even when some are null
        isConservative: true,
      };

      return [ratingDataset, conservativeRatingDataset];
    });

    return {
      labels,
      datasets
    };
  }, [players, playerHistories]);

  const chartData = useMemo(() => generateChartData(), [generateChartData]);

  const filteredChartData = useMemo(() => {
    if (!chartData) return null;
    return {
      ...chartData,
      datasets: chartData.datasets.filter((dataset: any) => {
        if (activeTab === 'ratings') {
          return !dataset.isConservative;
        } else { // conservative
          return dataset.isConservative;
        }
      })
    };
  }, [chartData, activeTab]);

  // Determine if we're on a touch device for zoom/pan configuration
  const touchDevice = useMemo(() => isTouchDevice(), []);

  const handleResetZoom = () => {
    if (chartRef.current) {
      // The resetZoom method is added by the zoom plugin
      const chart = chartRef.current as any;
      if (chart.resetZoom) {
        chart.resetZoom();
      }
      setIsZoomed(false);
    }
  };

  // Reset zoom state when switching tabs
  useEffect(() => {
    setIsZoomed(false);
  }, [activeTab]);

  // Set up zoom state tracking
  useEffect(() => {
    if (!chartRef.current || !filteredChartData) return;

    const chart = chartRef.current;
    const checkZoomState = () => {
      if (!chartRef.current || !filteredChartData) return;
      const xScale = chartRef.current.scales.x;
      const totalLabels = filteredChartData.labels.length;
      const isCurrentlyZoomed = Math.abs(xScale.min - 0) > 0.1 || Math.abs(xScale.max - (totalLabels - 1)) > 0.1;
      setIsZoomed(isCurrentlyZoomed);
    };

    // Check zoom state on mouse up (after zoom/pan interactions)
    const handleMouseUp = () => {
      setTimeout(checkZoomState, 100);
    };

    // Check zoom state on wheel (zoom via mouse wheel)
    const handleWheel = () => {
      setTimeout(checkZoomState, 100);
    };

    const canvas = chart.canvas;
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('wheel', handleWheel);
    canvas.addEventListener('touchend', handleMouseUp); // For touch devices

    return () => {
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('touchend', handleMouseUp);
    };
  }, [filteredChartData]);

  return (
    <div className={`bg-surface shadow rounded-lg p-6 ${className}`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h3 className="text-lg font-medium text-heading flex items-center">
          <ChartBarIcon className="h-5 w-5 mr-2" />
          {title}
        </h3>
        <div className="flex flex-col sm:flex-row gap-2">
          {showConservativeToggle && <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1 bg-surface-skeleton p-1 rounded-lg w-full sm:w-auto">
            <button
              onClick={() => setActiveTab('ratings')}
              className={`px-3 py-1 text-sm font-medium rounded-md ${activeTab === 'ratings' ? 'bg-surface shadow' : 'text-body hover:bg-surface-active'}`}
            >
              Ratings
            </button>
            <button
              onClick={() => setActiveTab('conservative')}
              className={`px-3 py-1 text-sm font-medium rounded-md ${activeTab === 'conservative' ? 'bg-surface shadow' : 'text-body hover:bg-surface-active'}`}
            >
              Conservative
            </button>
          </div>}
          {isZoomed && (
            <button
              onClick={handleResetZoom}
              className="px-3 py-1 text-sm font-medium rounded-md bg-primary-100 text-primary-700 hover:bg-primary-200 border border-primary-300"
            >
              Reset Zoom
            </button>
          )}
        </div>
      </div>
      {filteredChartData ? (
        <div className="h-96" style={{ touchAction: 'none' }}>
          <Line
            ref={chartRef}
            data={filteredChartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'top' as const,
                  labels: {
                    usePointStyle: true,
                    pointStyle: 'line',
                  },
                },
                title: {
                  display: true,
                  text: activeTab === 'ratings' ? 'Player Rating Progression Over Time' : 'Player Conservative Rating Progression Over Time',
                },
                zoom: {
                  zoom: {
                    drag: {
                      enabled: true,
                      modifierKey: null, // No modifier key needed, just drag
                    },
                    wheel: {
                      enabled: false, // Disable mouse wheel zoom
                    },
                    pinch: {
                      enabled: false, // Disable pinch zoom - use drag zoom only
                    },
                    mode: 'x' as const,
                    limits: {
                      x: {
                        min: 0,
                        max: filteredChartData.labels.length - 1,
                      },
                    },
                  },
                  pan: {
                    enabled: true,
                    mode: 'x' as const,
                    // On touch devices, allow panning without modifier key
                    // On desktop, require Shift key to distinguish from drag zoom
                    modifierKey: touchDevice ? undefined : 'shift',
                    limits: {
                      x: {
                        min: 0,
                        max: filteredChartData.labels.length - 1,
                      },
                    },
                  },
                } as any,
              },
              scales: {
                x: {
                  title: {
                    display: true,
                    text: 'Games Played (Chronological Order)',
                  },
                },
                y: {
                  title: {
                    display: true,
                    text: 'Rating',
                  },
                  beginAtZero: false,
                },
              },
              interaction: {
                intersect: false,
                mode: 'index' as const,
              },
            }}
          />
        </div>
      ) : (
        <div className="text-center py-8 text-muted">
          <ChartBarIcon className="h-12 w-12 mx-auto mb-4 text-disabled" />
          <p>{emptyMessage}</p>
          <p className="text-sm mt-2">Play some games to see rating progression!</p>
        </div>
      )}
    </div>
  );
};

export default PlayerRatingsChart;
